import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea,
  Chip, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton,
} from '@mui/material';
import Iconify from '../../../../components/Iconify';

const STATUS_COLOR = {
  paid: 'success', pending: 'warning', partially_paid: 'info',
  cancelled: 'default', overdue: 'error',
};

function InvoicePDFDialog({ invoice, open, onClose }) {
  if (!invoice) return null;
  const pdfUrl = invoice.pdf_url;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{invoice.invoice_number}</Typography>
          <Stack direction="row" spacing={1}>
            {pdfUrl && (
              <Button
                size="small"
                startIcon={<Iconify icon="mdi:download" />}
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </Button>
            )}
            <IconButton onClick={onClose} size="small"><Iconify icon="mdi:close" /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {pdfUrl ? (
          <Box
            component="iframe"
            src={pdfUrl}
            sx={{ width: '100%', height: '70vh', border: 0 }}
            title="Invoice PDF"
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Company</Typography>
                <Typography variant="body2">{invoice.company}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Amount</Typography>
                <Typography variant="body2" fontWeight={700}>{invoice.amount?.toLocaleString()}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography variant="body2">{invoice.due_date || '—'}</Typography>
              </Stack>
              {invoice.days_overdue > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="error">Days Overdue</Typography>
                  <Typography variant="body2" color="error">{invoice.days_overdue} days</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvoiceCard({ invoice }) {
  const [open, setOpen] = useState(false);
  const statusKey = invoice.days_overdue > 0 ? 'overdue' : invoice.status;
  return (
    <>
      <Card variant="outlined" sx={{ minWidth: 220, maxWidth: 260, flexShrink: 0, '&:hover': { boxShadow: 3 } }}>
        <CardActionArea onClick={() => setOpen(true)}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">{invoice.invoice_number}</Typography>
                <Chip
                  label={statusKey}
                  color={STATUS_COLOR[statusKey] || 'default'}
                  size="small"
                  sx={{ height: 18, fontSize: 10 }}
                />
              </Stack>
              <Typography variant="body2" fontWeight={600} noWrap>{invoice.company}</Typography>
              <Typography variant="subtitle2" color="primary">
                ₹{Number(invoice.amount || 0).toLocaleString()}
              </Typography>
              {invoice.due_date && (
                <Typography variant="caption" color={invoice.days_overdue > 0 ? 'error' : 'text.secondary'}>
                  Due: {invoice.due_date}
                  {invoice.days_overdue > 0 && ` (${invoice.days_overdue}d overdue)`}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon="mdi:file-pdf-box" sx={{ color: 'error.main', fontSize: 14 }} />
                <Typography variant="caption" color="text.secondary">Click to view</Typography>
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      <InvoicePDFDialog invoice={invoice} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default function InvoiceCards({ block }) {
  const { cards = [] } = block;
  return (
    <Box sx={{ my: 1.5 }}>
      {cards.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No invoices found.</Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
          {cards.map((inv) => <InvoiceCard key={inv.id || inv.invoice_number} invoice={inv} />)}
        </Box>
      )}
    </Box>
  );
}
